import { useCallback, useEffect, useState } from "react";
import { Plus, X, PackageOpen } from "lucide-react";
import api, { fmtDateTime, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-shipany-blue focus:outline-none";

export default function Inventory() {
  const [items, setItems] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [open, setOpen] = useState(false);
  const [adjusting, setAdjusting] = useState(null);
  const [form, setForm] = useState({ sku: "", name: "", available: 0, reorder_level: 5 });
  const [adj, setAdj] = useState({ bucket: "available", change: 1, reason: "" });
  const [tab, setTab] = useState("stock");

  const load = useCallback(() => {
    api.get("/inventory").then(({ data }) => setItems(data)).catch(() => {});
    api.get("/inventory/ledger").then(({ data }) => setLedger(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/inventory", { ...form, available: Number(form.available), reorder_level: Number(form.reorder_level) });
      toast.success("SKU added");
      setOpen(false);
      setForm({ sku: "", name: "", available: 0, reorder_level: 5 });
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const adjust = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/inventory/${adjusting.item_id}/adjust`, { ...adj, change: Number(adj.change) });
      toast.success("Stock adjusted");
      setAdjusting(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div data-testid="inventory-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Inventory</h1>
          <p className="text-sm text-slate-500">SKU stock with full ledger. Bookings auto-reserve matching SKUs.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="inventory-add-btn" className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] glow-blue">
          <Plus className="h-4 w-4" /> Add SKU
        </button>
      </div>

      <div className="mt-5 flex gap-2">
        {[["stock", "Stock"], ["ledger", "Ledger"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`inventory-tab-${k}`}
            className={`rounded-xl px-5 py-2.5 text-sm font-bold ${tab === k ? "bg-shipany-navy text-white" : "border border-slate-200 bg-white text-slate-500"}`}>{l}</button>
        ))}
      </div>

      {tab === "stock" && (
        <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="inventory-table">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white">
                <th className="px-5 py-3.5">SKU</th><th className="px-4 py-3.5">Product</th><th className="px-4 py-3.5">Available</th>
                <th className="px-4 py-3.5">Reserved</th><th className="px-4 py-3.5">Damaged</th><th className="px-4 py-3.5">Returned</th><th className="px-4 py-3.5"></th>
              </tr>
            </thead>
            <tbody>
              {(items || []).map((it) => (
                <tr key={it.item_id} className="border-t border-slate-100" data-testid={`inventory-row-${it.sku}`}>
                  <td className="mono px-5 py-3 font-semibold text-shipany-blue">{it.sku}</td>
                  <td className="px-4 py-3">{it.name}
                    {it.available <= it.reorder_level && <span className="ml-2 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-600">LOW STOCK</span>}
                  </td>
                  <td className="mono px-4 py-3 font-bold text-emerald-600">{it.available}</td>
                  <td className="mono px-4 py-3 text-amber-600">{it.reserved}</td>
                  <td className="mono px-4 py-3 text-red-500">{it.damaged}</td>
                  <td className="mono px-4 py-3 text-slate-500">{it.returned}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => { setAdjusting(it); setAdj({ bucket: "available", change: 1, reason: "" }); }} data-testid={`inventory-adjust-${it.sku}`}
                      className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-shipany-blue hover:bg-shipany-blue/5">Adjust</button>
                  </td>
                </tr>
              ))}
              {items && items.length === 0 && <tr><td colSpan={7} className="px-6 py-10 text-center text-slate-400">No SKUs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "ledger" && (
        <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="inventory-ledger">
          <table className="w-full min-w-[640px] text-sm">
            <thead><tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white"><th className="px-5 py-3">SKU</th><th className="px-4 py-3">Bucket</th><th className="px-4 py-3">Change</th><th className="px-4 py-3">Reason</th><th className="px-4 py-3">When</th></tr></thead>
            <tbody>
              {ledger.map((l) => (
                <tr key={l.entry_id} className="border-t border-slate-100">
                  <td className="mono px-5 py-3 font-semibold text-shipany-blue">{l.sku}</td>
                  <td className="px-4 py-3 capitalize text-slate-500">{l.bucket}</td>
                  <td className={`mono px-4 py-3 font-bold ${l.change > 0 ? "text-emerald-600" : "text-red-500"}`}>{l.change > 0 ? "+" : ""}{l.change}</td>
                  <td className="px-4 py-3 text-slate-500">{l.reason}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{fmtDateTime(l.created_at)}</td>
                </tr>
              ))}
              {ledger.length === 0 && <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400">No ledger entries.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="inventory-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <form onSubmit={create} className="relative w-full max-w-sm rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between"><h2 className="font-display text-lg font-bold text-shipany-navy">Add SKU</h2><button type="button" onClick={() => setOpen(false)} className="text-slate-400"><X className="h-5 w-5" /></button></div>
            <div className="mt-4 space-y-3.5">
              <input data-testid="inv-sku" className={`${inputCls} mono`} placeholder="SKU *" required value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
              <input data-testid="inv-name" className={inputCls} placeholder="Product name *" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="inv-available" type="number" className={`${inputCls} mono`} placeholder="Opening stock" value={form.available} onChange={(e) => setForm({ ...form, available: e.target.value })} />
                <input data-testid="inv-reorder" type="number" className={`${inputCls} mono`} placeholder="Reorder level" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} />
              </div>
            </div>
            <button data-testid="inv-save-btn" className="mt-5 w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3]">Add SKU</button>
          </form>
        </div>
      )}

      {adjusting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="inventory-adjust-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setAdjusting(null)} />
          <form onSubmit={adjust} className="relative w-full max-w-sm rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between"><h2 className="font-display text-lg font-bold text-shipany-navy"><PackageOpen className="mr-1 inline h-5 w-5 text-shipany-blue" />{adjusting.sku}</h2><button type="button" onClick={() => setAdjusting(null)} className="text-slate-400"><X className="h-5 w-5" /></button></div>
            <div className="mt-4 space-y-3.5">
              <select data-testid="adj-bucket" className={inputCls} value={adj.bucket} onChange={(e) => setAdj({ ...adj, bucket: e.target.value })}>
                <option value="available">Available</option><option value="reserved">Reserved</option><option value="damaged">Damaged</option><option value="returned">Returned</option>
              </select>
              <input data-testid="adj-change" type="number" className={`${inputCls} mono`} value={adj.change} onChange={(e) => setAdj({ ...adj, change: e.target.value })} placeholder="± quantity" />
              <input data-testid="adj-reason" className={inputCls} placeholder="Reason" value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} />
            </div>
            <button data-testid="adj-save-btn" className="mt-5 w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3]">Apply</button>
          </form>
        </div>
      )}
    </div>
  );
}
